import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import LandingPage from '../../LandingPage';
import { getLandingPage, LANDING_PAGE_REQUEST_TIMEOUT_MS } from '../../hooks/useLandingPages';
import type { LandingPageData } from '../../types';
import { resolveLandingMarketCode } from '../../landingPage/CustomLanding';

const COLORADO_RECALL_REFRESH_INTERVAL_MS = 60_000;

export default function LandingPageContainer() {
  const params = useParams();
  const issuanceId = String(params.issuanceId || '').trim();

  const landingIndex = useMemo(() => {
    const raw = params.index;
    if (raw == null || raw.trim() === '') return 0;

    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    return parsed;
  }, [params.index]);

  const [landingPage, setLandingPage] = useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(Boolean(issuanceId));
  const [error, setError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  useEffect(() => {
    if (!issuanceId) {
      setLandingPage(null);
      setLoading(false);
      setError(null);
      setRefreshError(null);
      return;
    }

    let cancelled = false;
    let inFlight = false;
    let intervalId: number | undefined;
    let activeRequest: AbortController | undefined;
    setLoading(true);
    setError(null);
    setRefreshError(null);

    async function loadLandingPage(isRefresh: boolean) {
      if (cancelled || inFlight) return;
      inFlight = true;
      const controller = new AbortController();
      activeRequest = controller;
      const aborted = new Promise<never>((_, reject) => {
        controller.signal.addEventListener('abort', () => reject(controller.signal.reason), { once: true });
      });
      const timeoutId = window.setTimeout(() => {
        controller.abort(new Error('Landing page request timed out after 30 seconds'));
      }, LANDING_PAGE_REQUEST_TIMEOUT_MS);
      try {
        const data = await Promise.race([
          getLandingPage(issuanceId, landingIndex, controller.signal),
          aborted,
        ]);
        if (cancelled) return;
        if (!data) throw new Error('Landing page not found');
        setLandingPage(data);
        setRefreshError(null);

        if (resolveLandingMarketCode(data.marketCode, data.regulatoryCard) === 'CO') {
          if (intervalId === undefined) {
            intervalId = window.setInterval(() => {
              void loadLandingPage(true);
            }, COLORADO_RECALL_REFRESH_INTERVAL_MS);
          }
        } else if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load landing page';
        if (isRefresh) {
          setRefreshError(`Unable to refresh recall information: ${message}. Previously loaded information may be out of date. Retrying automatically every 60 seconds.`);
        } else {
          setError(message);
        }
      } finally {
        window.clearTimeout(timeoutId);
        activeRequest = undefined;
        inFlight = false;
        if (!cancelled && !isRefresh) setLoading(false);
      }
    }

    void loadLandingPage(false);

    return () => {
      cancelled = true;
      if (intervalId !== undefined) window.clearInterval(intervalId);
      activeRequest?.abort(new Error('Landing page request cancelled'));
    };
  }, [issuanceId, landingIndex]);

  if (!issuanceId) {
    return (
      <div className="lp-root">
        <main className="lp-main">
          <div className="lp-content">Missing landing page id</div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="lp-root">
        <main className="lp-main">
          <div className="lp-content">Loading…</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lp-root">
        <main className="lp-main">
          <div className="lp-content">{error}</div>
        </main>
      </div>
    );
  }

  return (
    <>
      <LandingPage
        productCard={landingPage?.productCard}
        brandCard={landingPage?.brandCard}
        coaCard={landingPage?.coaCard}
        regulatoryCard={landingPage?.regulatoryCard}
        packageLabel={landingPage?.packageLabel}
        isStaged={landingPage?.isStaged}
        stagedProducerName={landingPage?.stagedProducerName}
        stagedProductName={landingPage?.stagedProductName}
        issuanceId={issuanceId}
        landingIndex={landingIndex}
        facilityName={landingPage?.facilityName}
        facilityLicense={landingPage?.facilityLicense}
        isOnRecall={landingPage?.isOnRecall}
        publicHealthNoticeUrls={landingPage?.publicHealthNoticeUrls}
        marketCode={landingPage?.marketCode}
        confirmedDates={landingPage?.confirmedDates}
        layout={landingPage?.layout}
      />
      {refreshError && <div className="lp-content" role="alert">{refreshError}</div>}
    </>
  );
}
